import React, { ComponentType, useCallback } from 'react';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { getFilteredEventInfosSelector } from '@om-fe/core/components/event-slider/event-slider.selectors';
import { EventSliderStoreMainModel } from '@om-fe/core/components/event-slider/event-slider.store';
import getSlice from '@om-fe/core/utils/get-slice';
import { BemMethod, createBem } from '@om-fe/utils';
import { FilteredEventInfos, MatchListStoreModel } from '../match-list/utils/match-list-models';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';

export interface RedCardsInfoElementPublicProps {
    readonly sliceKey: string;
    readonly redCardsId?: string;
    readonly homeParticipantId?: string;
    readonly isEventSlider?: boolean;
}

interface RedCardsInfoElementStoreProps {
    readonly redCardsElement?: FilteredEventInfos;
}

interface RedCardsObj {
    readonly home: string | number;
    readonly away: string | number;
}

export type RedCardsInfoElementProps = RedCardsInfoElementPublicProps & RedCardsInfoElementStoreProps;

const bem: BemMethod = createBem({name: 'RedCards'});

function RedCardsInfoElementComponent({homeParticipantId, redCardsElement}: RedCardsInfoElementProps) {
    const pickCorrectParticipant = useCallback(() => {
        let redCardsObj: RedCardsObj = {
            home: 0,
            away: 0,
        };

        if (redCardsElement && homeParticipantId === redCardsElement.paramParticipantId2) {
            redCardsObj = {
                home: redCardsElement.paramFloat2,
                away: redCardsElement.paramFloat1,
            };
        } else if (redCardsElement && homeParticipantId === redCardsElement.paramParticipantId1) {
            redCardsObj = {
                home: redCardsElement.paramFloat1,
                away: redCardsElement.paramFloat2,
            };
        }

        return (
            <span className={bem({element: 'PartGroup'})}>
                <span className={bem({
                    element: 'Part',
                    modifiers: {
                        Home: true,
                        Zero: !redCardsObj.home
                    }
                })}>
                    <span className={bem({element: 'Icon'})}>
                        <GeneralIcon name='card-red' />
                    </span>
                    <span className={bem({element: 'Counter'})}>
                        {redCardsObj.home}
                    </span>
                </span>
                <span className={bem({
                    element: 'Part',
                    modifiers: {
                        Away:  true,
                        Zero: !redCardsObj.away
                    }
                })}>
                    <span className={bem({element: "Icon"})}>
                        <GeneralIcon name='card-red' />
                    </span>
                    <span className={bem({element: 'Counter'})}>
                        {redCardsObj.away}
                    </span>
                </span>
            </span>
        );
    }, [homeParticipantId, redCardsElement])

    if (!redCardsElement) {
        return null;
    }

    return (pickCorrectParticipant());
}

const RedCardsInfoElement: ComponentType<RedCardsInfoElementPublicProps> = connect<RedCardsInfoElementStoreProps, undefined, RedCardsInfoElementPublicProps>(() => {
    const eventSliderFilteredEventInfos = getFilteredEventInfosSelector();

    return (state: AppState, props: RedCardsInfoElementPublicProps): RedCardsInfoElementStoreProps => {
        const slice: EventSliderStoreMainModel | MatchListStoreModel | undefined = getSlice<EventSliderStoreMainModel | MatchListStoreModel>(state, props.sliceKey);
        const { isEventSlider, redCardsId } = props;
        if (!slice || !redCardsId) {
            return { redCardsElement: undefined};
        }
        const redCardsElement: FilteredEventInfos | undefined = isEventSlider
            ? eventSliderFilteredEventInfos(slice as EventSliderStoreMainModel)[redCardsId]
            : ((slice as MatchListStoreModel).filteredEventInfos[redCardsId]);

        return {
            redCardsElement,
        };
    };
})(RedCardsInfoElementComponent);

export default RedCardsInfoElement;
